import React, { useEffect, useMemo, useRef, useState } from 'react';
import { storyblokEditable } from 'gatsby-source-storyblok';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { Mousewheel, Navigation } from 'swiper/modules';
import { Link } from 'gatsby';

import S from './ConfiguratorSmall.styled';
import { H5, H6 } from '@/styles/typography';
import { Flex, MainInner, MainSection } from '@/styles/global';
import { Absolute, Icon, StyledButton } from '@/components/global';
import useProductsData from '@/src/data/useProductsData';
import { parseData, getContrastingTextColor } from '@/src/helpers';
import { IColor, IPackage, IProduct, IVersion } from '@/src/types';
import useVersionsData from '@/src/data/useVersionsData';
import useColorsData from '@/src/data/useColorsData';
import usePackageData from '@/src/data/usePackageData';
import { modifyImage } from '@/src/helpers/modifyImage';
import TitleGroup from '@/components/global/TitleGroup';
import { useSiteSettings } from '@/src/context/SiteSettingsContext';

interface ICustomProduct {
  version?: IVersion;
  color?: IColor;
  package?: IPackageContent;
}

interface IPackageContent {
  package: string;
  specifications: {
    name: string;
    value: string;
  }[];
}

export interface IConfiguratorSmallProps {
  blok: {
    bold_title: string;
    regular_title: string;
  };
  margin?: boolean;
}

const ConfiguratorSmall: React.FC<IConfiguratorSmallProps> = ({
  blok,
  blok: { bold_title, regular_title },
  margin,
}) => {
  const { slug } = useSiteSettings();
  const [link, setLink] = useState('');
  const productData = useProductsData(slug);
  const products: IProduct[] = parseData(productData);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [activeProduct, setActiveProduct] = useState<IProduct>(products[0]);
  const {
    packages: productPackages,
    versions: productVersions,
    colors: productColors,
    images: productImages,
  } = activeProduct.content;

  const packages: IPackage[] = parseData(usePackageData(slug));

  const versions: IVersion[] = parseData(useVersionsData(slug));
  const [versionOptions, setVersionOptions] = useState<IVersion[]>([]);

  const colors: IColor[] = parseData(useColorsData(slug));

  const [colorOptions, setColorOptions] = useState<IColor[]>([]);

  const [customProduct, setCustomProduct] = useState<
    ICustomProduct | undefined
  >(undefined);

  useEffect(() => {
    if (!customProduct?.color && colorOptions.length > 0) {
      setCustomProduct({
        version: versionOptions[0],
        package: productPackages?.[0] || packages[0].content,
        color: colorOptions[0],
      });
    }
  }, [colorOptions, versionOptions, packages]);

  // Check if mobile when resizing
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 820);
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check product in url, not params only url, and set to acitve
  useEffect(() => {
    const url = window.location.pathname;
    const product = products.find((product) => url.includes(product.slug));

    if (product) {
      setActiveProduct(product);
    }
  }, []);

  // OPTIONS AND DEFAULTS
  useEffect(() => {
    const filterAndSort = <T extends { uuid: string }>(
      items: T[],
      productItems: string[]
    ): T[] => {
      return items
        .filter((item) => productItems.includes(item.uuid))
        .sort(
          (a, b) =>
            productItems.findIndex((c) => c === a.uuid) -
            productItems.findIndex((c) => c === b.uuid)
        );
    };

    const versionObjects =
      productVersions && filterAndSort(versions, productVersions);
    const colorObjects =
      productColors &&
      filterAndSort(
        colors,
        productColors.map((c) => c.color)
      );

    versionObjects && setVersionOptions(versionObjects);
    setColorOptions(colorObjects);

    const productHasCurrentColor = productColors?.some(
      (color) => color.color === customProduct?.color?.uuid
    );

    const productHasCurrentVersion = productVersions?.some(
      (version) => version === customProduct?.version?.uuid
    );

    setCustomProduct({
      ...customProduct,
      package: productPackages?.[0] || packages[0].content,
      color: productHasCurrentColor ? customProduct?.color : colorObjects?.[0],
      version: productHasCurrentVersion
        ? customProduct?.version
        : versionObjects?.[0] || undefined,
    });
  }, [activeProduct]);

  // IMAGES
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const images = useMemo(() => {
    return productImages.find((image) => {
      if (!customProduct?.version)
        return image.color === customProduct?.color?.uuid;

      return (
        image.color === customProduct?.color?.uuid &&
        image.version === customProduct?.version?.uuid
      );
    })?.images;
  }, [productImages, customProduct?.color, customProduct?.version]);

  // SWIPER
  const swiperRef = useRef<SwiperRef | null>(null);

  const handlePrev = () => {
    if (swiperRef.current) swiperRef.current.swiper.slidePrev();
  };

  const handleNext = () => {
    if (swiperRef.current) swiperRef.current.swiper.slideNext();
  };

  const renderOption = (
    key: string,
    title: string,
    isActive: boolean,
    onClick: React.MouseEventHandler<HTMLDivElement>
  ) => {
    return (
      <S.Option key={key} $active={isActive} onClick={onClick} tabIndex={0}>
        <Absolute>
          <S.BreadNormal className="title">{title}</S.BreadNormal>
        </Absolute>
        <S.HiddenTitle>
          <S.BreadNormal $weight={350}>{title}</S.BreadNormal>
        </S.HiddenTitle>
      </S.Option>
    );
  };

  useEffect(() => {
    const params = new URLSearchParams();
    params.append('color', customProduct?.color?.uuid || '');
    params.append('version', customProduct?.version?.uuid || '');
    params.append('package', customProduct?.package?.package || '');

    const url = `${slug}/configurator/${activeProduct.slug}/?${params}`;

    setLink(url);
  }, [customProduct]);

  return (
    <MainSection
      $noMargin
      $center
      $color="lightGrey"
      {...storyblokEditable(blok)}
    >
      <Flex $column style={{ backgroundColor: '#F5F4F4' }} $width={'100%'}>
        <MainInner>
          <Flex
            $column
            $desktopDir="row"
            $justify="between"
            $gap={24}
            $width="100%"
            $breakpoint="l"
            $margin={margin ? '100px 0 0 0' : '54px 0 0 0'}
            $marginDesktop={margin ? '150px 0 0 0' : '71px 0 0 0'}
          >
            <Flex $column>
              <TitleGroup
                boldTitle={bold_title}
                regularTitle={regular_title}
                type="h3"
              />
              <S.Button $desktop>
                <Link to={link}>
                  <StyledButton
                    label="Open the configurator"
                    margin="20px 0 0 0"
                    color="black"
                  />
                </Link>
              </S.Button>
            </Flex>

            <Flex
              $gap={17}
              $column
              $breakpoint="l"
              $width="100%"
              $desktopWidth="auto"
              $desktopAlign={'end'}
            >
              {products.length > 0 && (
                <S.Options $maxWidth>
                  {products.map((product) => {
                    const isActive = activeProduct?.uuid === product?.uuid;

                    return renderOption(
                      product.uuid,
                      product.name,
                      isActive,
                      () => setActiveProduct(product)
                    );
                  })}
                </S.Options>
              )}

              <Flex
                $gap={17}
                $column
                $desktopDir="row"
                $width={'100%'}
                $desktopWidth={'fit-content'}
              >
                {versionOptions.length > 0 && (
                  <S.Options>
                    {versionOptions.map((version) => {
                      const isActive =
                        customProduct?.version?.uuid === version?.uuid;

                      return renderOption(
                        version.uuid,
                        version.name,
                        isActive,
                        () => {
                          setCustomProduct({
                            ...customProduct,
                            version: version,
                          });
                        }
                      );
                    })}
                  </S.Options>
                )}
                {colorOptions.length > 0 && (
                  <S.Options>
                    <Flex $gap={11} $margin="5px 10px">
                      {colorOptions.map((color, i) => {
                        const {
                          content: { hex },
                        } = color;
                        const isActive =
                          customProduct?.color?.uuid === color?.uuid;

                        return (
                          <S.ColorOption
                            key={i}
                            $active={isActive}
                            $color={hex}
                            tabIndex={0}
                            onClick={() => {
                              setCustomProduct({
                                ...customProduct,
                                color: color,
                              });
                            }}
                            aria-label={color.name}
                          >
                            <Icon
                              src={'Checkmark'}
                              color={getContrastingTextColor(hex)}
                            />
                          </S.ColorOption>
                        );
                      })}
                    </Flex>
                  </S.Options>
                )}
              </Flex>
            </Flex>
          </Flex>
        </MainInner>

        <S.Wrapper>
          {images && (
            <Swiper
              modules={[Navigation, Mousewheel]}
              ref={swiperRef}
              loop={true}
              spaceBetween={0}
              onRealIndexChange={() => {
                const swiperIndex = swiperRef.current?.swiper.realIndex;
                setActiveImageIndex(
                  swiperIndex ? swiperIndex % images.length : 0
                );
              }}
              slidesPerView={'auto'}
              centeredSlides={true}
              className="swiper"
              navigation={{
                nextEl: '.custom-next',
                prevEl: '.custom-prev',
              }}
            >
              {images?.concat(images)?.map((image, index) => {
                const modImage = modifyImage(
                  image,
                  isMobile ? '2600x2060' : '2600x1500'
                );

                return (
                  <SwiperSlide key={index}>
                    <S.Image src={modImage} alt={image.alt} />
                  </SwiperSlide>
                );
              })}
            </Swiper>
          )}
          <Absolute $left={0} $right={0} $top="50%" $zIndex={10}>
            <MainInner>
              <S.NavButton
                $left={15}
                $leftDesktop={20}
                tabIndex={0}
                onClick={handlePrev}
                className="custom-swiper-button custom-prev"
                aria-label="Previous image"
              >
                <Icon src={'ChevronLeft'} width={12} />
              </S.NavButton>
              <S.NavButton
                $right={15}
                $rightDesktop={20}
                tabIndex={0}
                onClick={handleNext}
                className="custom-swiper-button custom-next"
                aria-label="Next image"
              >
                <Icon src={'ChevronRight'} width={12} />
              </S.NavButton>
            </MainInner>
          </Absolute>
        </S.Wrapper>

        <MainInner>
          <Flex
            $dir="column-reverse"
            $align="end"
            $desktopAlign="start"
            $desktopDir="row"
            $desktopJustify="between"
            $margin="0 0 50px 0"
            $marginDesktop="0 0 71px 0"
            $width="100%"
            $gap={24}
          >
            <S.Specifications>
              {customProduct?.package?.specifications?.map(
                (specification, i) => {
                  return (
                    <Flex $column key={i}>
                      <H5 $weight={400}>{specification.value}</H5>
                      <H6>{specification.name}</H6>
                    </Flex>
                  );
                }
              )}
            </S.Specifications>
            <Flex $gap={10} $margin={'0 auto'}>
              {images?.map((_, index) => {
                return (
                  <S.PaginationDot
                    $active={activeImageIndex === index}
                    key={index}
                  />
                );
              })}
            </Flex>
          </Flex>
          <S.Button>
            <Link to={link} style={{ width: '100%' }}>
              <StyledButton
                label="Open the configurator"
                color="black"
                fullWidth
              />
            </Link>
          </S.Button>
        </MainInner>
      </Flex>
    </MainSection>
  );
};

export default ConfiguratorSmall;
